import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { sendContactWebhook } from '../lib/webhook';

const { FiMail, FiUser, FiBriefcase, FiFileText, FiSend, FiCheckCircle, FiAlertCircle, FiPhone, FiCalendar } = FiIcons;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    primary_telephone: '',
    company: '',
    receiptsPerMonth: '',
    message: '',
    gdpr_consent: false
  });
  
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openPrivacyPolicy = () => {
    window.open('#/datenschutz', 'datenschutz', 'width=800,height=600,scrollbars=yes');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ loading: true, success: false, error: null });

    try {
      const contactData = {
        // Standardisierte Variablen für Make/Boost.space
        vorname: formData.vorname,
        nachname: formData.nachname,
        name: `${formData.vorname} ${formData.nachname}`.trim(),
        email: formData.email,
        primary_telephone: formData.primary_telephone,
        company: formData.company || null,
        receipts_per_month: formData.receiptsPerMonth || null,
        message: formData.message || null,
        gdpr_consent: formData.gdpr_consent,
        
        // Zusätzliche Metadaten
        source_section: 'contact_section',
        lead_source: 'website_contact_form',
        contact_reason: 'general_inquiry'
      };

      // Send webhook data with GDPR compliance
      await sendContactWebhook(contactData);

      // Clear the form and set success
      setFormData({
        vorname: '',
        nachname: '',
        email: '',
        primary_telephone: '',
        company: '',
        receiptsPerMonth: '',
        message: '',
        gdpr_consent: false
      });
      
      setSubmitStatus({ loading: false, success: true, error: null });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(prev => ({ ...prev, success: false }));
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        loading: false, 
        success: false, 
        error: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.' 
      });
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }} 
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Lass uns sprechen - ganz in Ruhe
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-6">
            Im Erstgespräch schauen wir gemeinsam, wo du gerade stehst und was dir im Moment am meisten helfen würde.<br />
            Du musst nichts vorbereiten. Erzähle einfach, wie es dir mit deiner Buchhaltung gerade geht.<br />
            Wir finden zusammen heraus, welcher nächste Schritt sinnvoll ist.<br /><br />
            <motion.a 
              href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/Gb05r41McyYVyGQE/form" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              className="bg-[#E30613] hover:bg-[#C10511] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 inline-block shadow-lg mt-4"
            >
              Erstgespräch vereinbaren
            </motion.a><br /><br />
            Das Gespräch ist kostenfrei und unverbindlich.
          </p>
        </motion.div>

        {/* Centered form block */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }} 
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <p className="text-center text-xl text-gray-600 mb-8">
            Oder du schreibst mir kurz, was dich gerade beschäftigt:
          </p>
          <div className="bg-[#F3F1EE] p-8 rounded-xl shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitStatus.error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded">
                  <div className="flex items-center">
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 mr-2" />
                    <p className="text-red-700">{submitStatus.error}</p>
                  </div>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <SafeIcon icon={FiUser} className="inline mr-2" /> Vorname *
                  </label>
                  <input
                    type="text"
                    name="vorname"
                    value={formData.vorname}
                    onChange={handleInputChange}
                    required
                    disabled={submitStatus.loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="Dein Vorname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <SafeIcon icon={FiUser} className="inline mr-2" /> Nachname *
                  </label>
                  <input
                    type="text"
                    name="nachname"
                    value={formData.nachname}
                    onChange={handleInputChange}
                    required
                    disabled={submitStatus.loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                    placeholder="Dein Nachname"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="inline mr-2" /> E-Mail *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={submitStatus.loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  placeholder="deine@email.de"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiPhone} className="inline mr-2" /> Telefonnummer *
                </label>
                <input
                  type="tel"
                  name="primary_telephone"
                  value={formData.primary_telephone}
                  onChange={handleInputChange}
                  required
                  disabled={submitStatus.loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  placeholder="0123 456789"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiBriefcase} className="inline mr-2" /> Firma
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  disabled={submitStatus.loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  placeholder="Dein Unternehmen"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiFileText} className="inline mr-2" /> Belege pro Monat (ca.)
                </label>
                <select
                  name="receiptsPerMonth"
                  value={formData.receiptsPerMonth}
                  onChange={handleInputChange}
                  disabled={submitStatus.loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Bitte wählen</option>
                  <option value="0-50">0-50 Belege</option>
                  <option value="51-100">51-100 Belege</option>
                  <option value="101-300">101-300 Belege</option>
                  <option value="300+">Mehr als 300 Belege</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nachricht
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={submitStatus.loading}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  placeholder="Beschreibe kurz deine aktuelle Situation oder stelle Fragen..."
                />
              </div>
              
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="gdpr_consent"
                  checked={formData.gdpr_consent}
                  onChange={handleInputChange}
                  required
                  disabled={submitStatus.loading}
                  className="mt-1 w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-600">
                  Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
                  <button 
                    type="button" 
                    onClick={openPrivacyPolicy} 
                    className="text-red-500 hover:underline"
                  >
                    Datenschutzerklärung
                  </button>
                  {' '}zu und bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und verarbeitet werden. *
                </span>
              </div>
              
              <motion.button
                type="submit"
                disabled={submitStatus.loading}
                whileHover={{ scale: submitStatus.loading ? 1 : 1.05 }}
                whileTap={{ scale: submitStatus.loading ? 1 : 0.95 }}
                className={`w-full bg-[#E30613] hover:bg-[#C10511] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center space-x-2`}
              >
                {submitStatus.loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Wird gesendet...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="text-xl" />
                    <span>Nachricht senden</span>
                  </>
                )}
              </motion.button>
            </form>
          </div>
          
          <p className="text-center text-gray-600 mt-6">
            Du bekommst in der Regel innerhalb von 24 Stunden eine Rückmeldung.
          </p>
        </motion.div>
      </div>

      {/* Success Popup */}
      {submitStatus.success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center z-50"
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="relative bg-[#F3F1EE] rounded-lg p-8 max-w-md w-full mx-4 text-center z-10"
          >
            <div className="flex flex-col items-center">
              <SafeIcon icon={FiCheckCircle} className="text-5xl text-[#1A1A1A] mb-4" />
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">
                Vielen Dank
              </h3>
              <p className="text-[#1A1A1A]">
                dein Formular wurde erfolgreich gesendet.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default ContactSection;